import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SessionDemographics {
  timezone: string;
  userAgent: string;
  screenResolution: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  browserName: string;
  browserVersion: string;
  osName: string;
  language: string;
}

interface SessionContextType {
  sessionKey: string;
  sessionStartTime: Date | null;
}

const SessionContext = createContext<SessionContextType | null>(null);

const captureDemographics = (): SessionDemographics => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const userAgent = navigator.userAgent;
  const screenResolution = `${window.screen.width}x${window.screen.height}`;
  const language = navigator.language;
  
  // Device type detection
  const width = window.screen.width;
  let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  if (width < 768) deviceType = 'mobile';
  else if (width < 1024) deviceType = 'tablet';
  
  // Browser detection
  let browserName = 'Unknown';
  let browserVersion = '';
  if (userAgent.includes('Chrome')) {
    browserName = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (userAgent.includes('Firefox')) {
    browserName = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browserName = 'Safari';
    const match = userAgent.match(/Version\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (userAgent.includes('Edge')) {
    browserName = 'Edge';
    const match = userAgent.match(/Edge\/(\d+)/);
    browserVersion = match ? match[1] : '';
  }
  
  // OS detection
  let osName = 'Unknown';
  if (userAgent.includes('Windows')) osName = 'Windows';
  else if (userAgent.includes('Mac')) osName = 'macOS';
  else if (userAgent.includes('Linux')) osName = 'Linux';
  else if (userAgent.includes('Android')) osName = 'Android';
  else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) osName = 'iOS';
  
  return {
    timezone,
    userAgent,
    screenResolution,
    deviceType,
    browserName,
    browserVersion,
    osName,
    language
  };
};

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const [sessionKey, setSessionKey] = useState<string>('');
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [initialized, setInitialized] = useState(false);

  const generateNewSession = async () => {
    const key = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('session_key', key);
    setSessionKey(key);
    setSessionStartTime(new Date());
    
    // Track the new session
    try {
      const demographics = captureDemographics();
      await supabase.functions.invoke('track-session', {
        body: { sessionKey: key, ...demographics }
      });
    } catch (error) {
      console.error('Error tracking session:', error);
    }
  };

  // Listen for auth changes to regenerate session on logout
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        // Generate new session key for fresh anonymous tracking
        generateNewSession();
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    // Only run once
    if (initialized) return;
    
    const initializeSession = async () => {
      let key = localStorage.getItem('session_key');
      
      if (!key) {
        key = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('session_key', key);
      }

      setSessionKey(key);
      setSessionStartTime(new Date());
      setInitialized(true);
      
      // Call track-session ONCE at app initialization
      try {
        const demographics = captureDemographics();
        await supabase.functions.invoke('track-session', {
          body: { sessionKey: key, ...demographics }
        });
      } catch (error) {
        console.error('Error tracking session:', error);
      }
    };
    
    initializeSession();
  }, [initialized]);

  return (
    <SessionContext.Provider value={{ sessionKey, sessionStartTime }}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSessionContext = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSessionContext must be used within SessionProvider');
  }
  return context;
};
